<template>
  <div class="signup-page">
    <div class="signup-card">

      <!-- Header -->
      <div class="signup-header">
        <img src="@/assets/images/logot.svg" class="login-logo-m" alt="Decast" />
        <h1>Create your account</h1>
        <p class="signup-subtitle">Join Decast - own your identity</p>
      </div>

      <!-- DecastID - hero -->
      <div class="did-hero">
        <DidsLogin />
        <a class="did-learn" @click="$router.push('/what-is-did')">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.3"/><path d="M8 7v4M8 5.5h.01" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
          What's a DecastID?
        </a>
      </div>

      <!-- Divider -->
      <div class="sep">
        <hr /><span>or</span><hr />
      </div>

      <!-- Social logins -->
      <div class="social-stack">
        <GoogleLogin />
        <MetamaskLogin />
      </div>

      <!-- Email link -->
      <a class="email-link" @click="showEmailModal = true">Sign up with email instead</a>

      <!-- Footer -->
      <p class="signup-footer">
        Already have an account?&nbsp;<a @click="$router.push('/v2login')">Sign In</a>
      </p>

    </div>

    <!-- Email Registration Modal -->
    <transition name="fade">
      <div class="modal-overlay" v-if="showEmailModal" @click.self="showEmailModal = false">
        <div class="modal-card">
          <div class="modal-head">
            <h2>Sign up with email</h2>
            <button class="modal-close" @click="showEmailModal = false" type="button">&times;</button>
          </div>

          <div class="field-row">
            <div class="field">
              <label>First name</label>
              <input type="text" v-model="firstName" placeholder="John" @keyup.enter="handleSubmit" />
              <p v-if="firstNameError" class="field-error">{{ firstNameError }}</p>
            </div>
            <div class="field">
              <label>Last name</label>
              <input type="text" v-model="lastName" placeholder="Smith" @keyup.enter="handleSubmit" />
              <p v-if="lastNameError" class="field-error">{{ lastNameError }}</p>
            </div>
          </div>

          <div class="field">
            <label>Email address</label>
            <input type="email" v-model="email" placeholder="name@example.com" @keyup.enter="handleSubmit" />
            <p v-if="emailError" class="field-error">{{ emailError }}</p>
          </div>

          <div class="field">
            <label>Password</label>
            <div class="input-icon-wrap">
              <input :type="passwordInputType" v-model="password" placeholder="Create a password"
                @keyup.enter="handleSubmit" />
              <button class="eye-btn" @click="togglePasswordVisibility" type="button" tabindex="-1">
                <EyeVisible v-if="isPasswordVisible" />
                <EyeHide v-else />
              </button>
            </div>
            <p v-if="passwordError" class="field-error">{{ passwordError }}</p>
          </div>

          <label class="checkbox-row">
            <input type="checkbox" v-model="isTermsConditionAccepted" />
            <span>I agree to the <a href="#" class="terms-link">User Agreement</a> and <a href="/privacy" class="terms-link">Privacy Policy</a></span>
          </label>

          <button class="btn-primary" @click="handleSubmit">
            Create Account
          </button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import EyeHide from "@/decast/icons/EyeHide.vue";
import EyeVisible from "@/decast/icons/EyeVisible.vue";
import GoogleLogin from "@/decast/components/v2auth/social-logins/GoogleLogin.vue";
import MetamaskLogin from "@/decast/components/v2auth/social-logins/MetamaskLogin.vue";
import DidsLogin from "@/decast/components/v2auth/social-logins/DidsLogin.vue";

export default {
  data() {
    return {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      emailError: '',
      passwordError: '',
      firstNameError: '',
      lastNameError: '',
      isPasswordVisible: false,
      isTermsConditionAccepted: false,
      showEmailModal: false,
    };
  },
  components: {
    EyeHide,
    EyeVisible,
    GoogleLogin,
    MetamaskLogin,
    DidsLogin,
  },
  mounted() {
    document.getElementById('loading-bg-transparent').style.display = 'none';
  },
  computed: {
    passwordInputType() {
      return this.isPasswordVisible ? 'text' : 'password';
    },
  },
  methods: {
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
    handleSubmit() {
      this.firstNameError = '';
      this.lastNameError = '';
      this.emailError = '';
      this.passwordError = '';

      if (!this.firstName) {
        this.firstNameError = 'First name is required.';
        return;
      }
      if (!this.lastName) {
        this.lastNameError = 'Last name is required.';
        return;
      }
      if (!this.email) {
        this.emailError = 'Email is required.';
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.emailError = 'Enter a valid email address.';
        return;
      }
      if (!this.password) {
        this.passwordError = 'Password is required.';
        return;
      }
      if (!this.isTermsConditionAccepted) {
        this.$vs.notify({
          title: 'Action required!',
          text: 'Please accept Terms & Conditions',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
        });
        return;
      }
      this.registerUserJWt();
    },
    registerUserJWt() {
      document.getElementById('loading-bg-transparent-title').innerText = 'Creating your Decast account';
      document.getElementById('loading-bg-transparent').style.display = 'flex';
      const payload = {
        userDetails: {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          password: this.password,
        },
        notify: this.$vs.notify,
      };
      this.$store
        .dispatch('auth/v2registerUser', payload)
        .then((response) => {
          this.$vs.notify({
            title: 'Registration Successful',
            text: 'You have successfully registered!',
            iconPack: 'feather',
            icon: 'icon-check-circle',
            color: 'success',
          });
          document.getElementById('loading-bg-transparent').style.display = 'none';
          this.$router.push('/v2login');
        }).catch((error) => {
          const errors = error.response?.data?.errors;
          let text = "Something went wrong";
          if (errors) {
            if (typeof errors === "object" && !Array.isArray(errors)) {
              const firstKey = Object.keys(errors)[0];
              const firstValue = errors[firstKey];
              text = Array.isArray(firstValue) ? firstValue[0] : String(firstValue);
            } else if (typeof errors.message === "string") {
              text = errors.message;
            }
          }
          document.getElementById('loading-bg-transparent').style.display = 'none';
          this.$vs.notify({
            title: 'Registration Failed',
            text: text,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger',
          });
        });
    },
  }
}
</script>

<style scoped>
*:not(i) {
  font-family: 'Rethink Sans', -apple-system, BlinkMacSystemFont, sans-serif !important;
}

/* ── Page ── */
.signup-page {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  overflow-y: auto;
}

.signup-card {
  width: 100%;
  max-width: 380px;
}

/* ── Header ── */
.signup-header {
  margin-bottom: 28px;
}

.signup-header h1 {
  color: #FFFFFF;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
  letter-spacing: -0.01em;
}

.signup-subtitle {
  color: #6B6D7E;
  font-size: 14px;
  margin: 0;
  font-weight: 400;
}

/* ── DecastID hero ── */
.did-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
}

.did-learn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #4A4C5E;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.15s;
}

.did-learn:hover {
  color: #D7DF23;
}

.did-learn svg {
  flex-shrink: 0;
}

/* ── Separator ── */
.sep {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 24px 0;
}

.sep hr {
  flex: 1;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin: 0;
}

.sep span {
  color: #4A4C5E;
  font-size: 12px;
  white-space: nowrap;
}

/* ── Social stack ── */
.social-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Email link ── */
.email-link {
  display: block;
  text-align: center;
  color: #4A4C5E;
  font-size: 12px;
  cursor: pointer;
  margin-top: 20px;
  transition: color 0.15s;
}

.email-link:hover {
  color: #9B9DB0;
}

/* ── Footer ── */
.signup-footer {
  text-align: center;
  color: #4A4C5E;
  font-size: 13px;
  margin-top: 28px;
}

.signup-footer a {
  color: #D7DF23;
  cursor: pointer;
  font-weight: 600;
  transition: opacity 0.15s;
}

.signup-footer a:hover {
  opacity: 0.8;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-card {
  width: 100%;
  max-width: 420px;
  background: #1B1D29;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 28px 24px;
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.modal-head h2 {
  color: #FFFFFF;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: #6B6D7E;
  font-size: 22px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.15s;
}

.modal-close:hover {
  color: #FFFFFF;
}

/* ── Fields ── */
.field {
  margin-bottom: 16px;
}

.field-row {
  display: flex;
  gap: 12px;
}

.field-row .field {
  flex: 1;
}

.field label {
  font-size: 13px;
  font-weight: 500;
  color: #9B9DB0;
  display: block;
  margin-bottom: 6px;
}

.field input[type="email"],
.field input[type="text"],
.field input[type="password"] {
  width: 100%;
  height: 42px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #FFFFFF;
  font-size: 14px;
  padding: 0 14px;
  outline: none;
  transition: border-color 0.15s, background-color 0.15s;
}

.field input:hover {
  border-color: rgba(255, 255, 255, 0.14);
}

.field input:focus {
  border-color: rgba(215, 223, 35, 0.5);
  background: rgba(255, 255, 255, 0.06);
}

.field input::placeholder {
  color: #3E4050;
}

.field-error {
  color: #F87171;
  font-size: 12px;
  margin-top: 5px;
}

.input-icon-wrap {
  position: relative;
}

.input-icon-wrap input {
  padding-right: 42px !important;
}

.eye-btn {
  position: absolute;
  right: 0;
  top: 0;
  height: 42px;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #4A4C5E;
  padding: 0;
  transition: color 0.15s;
}

.eye-btn:hover {
  color: #9B9DB0;
}

/* ── Checkbox ── */
.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #6B6D7E;
  margin-bottom: 20px;
  line-height: 1.4;
}

.checkbox-row input[type="checkbox"] {
  width: 15px !important;
  height: 15px;
  accent-color: #D7DF23;
  cursor: pointer;
  margin-top: 1px;
  flex-shrink: 0;
}

.terms-link {
  color: #D7DF23;
  text-decoration: none;
  transition: opacity 0.15s;
}

.terms-link:hover {
  opacity: 0.8;
}

/* ── Button ── */
.btn-primary {
  width: 100%;
  height: 42px;
  background: #D7DF23;
  color: #0F1017;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.1s;
  font-family: 'Rethink Sans', sans-serif;
}

.btn-primary:hover {
  background: #C5CC1F;
}

.btn-primary:active {
  transform: scale(0.99);
}

/* ── Fade transition ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .signup-page {
    padding: 24px 16px;
  }

  .signup-header h1 {
    font-size: 21px;
  }

  .field-row {
    flex-direction: column;
    gap: 0;
  }
}

/* mobile branding: with the DecastID hero hidden the screen was bare */
.login-logo-m {
  display: none;
}
@media (max-width: 767px) {
  .login-logo-m {
    display: block;
    width: 56px;
    height: 56px;
    margin: 4px auto 18px;
  }
  .login-header,
  .signup-header {
    text-align: center;
  }
}
</style>

<style scoped>
/* did-hero-mobile-hide: DecastID needs the browser extension (desktop only) */
@media (max-width: 767px) {
  .did-hero,
  .sep {
    display: none !important;
  }
}
</style>
